
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Profile } from "../types";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useSupabaseUser } from "@/hooks/use-supabase-user";

export const useUsers = () => {
  const { isAdmin } = useUserRoles();
  const { user } = useSupabaseUser();

  // Check if user is admin - using the RPC function to bypass RLS
  const { data: isAdminCheck } = useQuery({
    queryKey: ["isAdmin"],
    queryFn: async () => {
      try {
        const { data: isAdminResult, error } = await supabase.rpc('is_admin', {
          user_id: (await supabase.auth.getUser()).data.user?.id
        });
        
        if (error) {
          console.error("Error checking admin role:", error);
          return false;
        }
        
        console.log("Is admin check result:", !!isAdminResult);
        return !!isAdminResult;
      } catch (err) {
        console.error("Error in isAdmin check:", err);
        return false;
      }
    },
  });

  // Fetch profiles - this query will respect RLS policies
  const { data: profiles, isLoading: isLoadingProfiles, refetch } = useQuery({
    queryKey: ["profiles", isAdmin, user?.id],
    queryFn: async () => {
      try {
        // First determine what data the user can access based on role
        if (isAdmin || isAdminCheck) {
          console.log("Admin user - fetching all profiles");
          // Admins can see all profiles
          const { data: allProfiles, error: profilesError } = await supabase
            .from("profiles")
            .select("*");

          if (profilesError) {
            console.error("Error fetching profiles:", profilesError);
            throw profilesError;
          }

          // Then get all user roles
          const { data: userRoles, error: userRolesError } = await supabase
            .from("user_roles")
            .select("user_id, role");

          if (userRolesError) {
            console.error("Error fetching user roles:", userRolesError);
            throw userRolesError;
          }

          // Map the roles to profiles
          const profilesWithRoles = allProfiles.map((profile) => ({
            ...profile,
            user_roles: userRoles
              .filter((role) => role.user_id === profile.id)
              .map((role) => ({ role: role.role })),
          }));

          return profilesWithRoles as Profile[];
        } else {
          console.log("Regular user - fetching profiles with RLS");
          // Regular users will only see what RLS allows
          const { data: visibleProfiles, error: profilesError } = await supabase
            .from("profiles")
            .select("*");

          if (profilesError) {
            console.error("Error fetching profiles with RLS:", profilesError);
            throw profilesError;
          }

          console.log("Profiles visible through RLS:", visibleProfiles?.length || 0);

          // For regular users, we still need to fetch roles for the profiles they can see
          if (visibleProfiles && visibleProfiles.length > 0) {
            const profileIds = visibleProfiles.map(profile => profile.id);
            
            const { data: userRoles, error: userRolesError } = await supabase
              .from("user_roles")
              .select("user_id, role")
              .in("user_id", profileIds);

            if (userRolesError) {
              console.error("Error fetching user roles for visible profiles:", userRolesError);
              throw userRolesError;
            }

            // Map the roles to profiles
            const profilesWithRoles = visibleProfiles.map((profile) => ({
              ...profile,
              user_roles: userRoles
                .filter((role) => role.user_id === profile.id)
                .map((role) => ({ role: role.role })),
            }));

            return profilesWithRoles as Profile[];
          }

          // If no visible profiles or no user roles, ensure we're returning an empty array with the correct shape
          return (visibleProfiles || []).map(profile => ({
            ...profile,
            user_roles: [] // Add empty user_roles array to match Profile type
          })) as Profile[];
        }
      } catch (error) {
        console.error("Error in fetchUsers:", error);
        return [];
      }
    },
    enabled: !!user, // Only run when user is available
  });

  return {
    isAdmin: isAdminCheck,
    profiles,
    isLoadingProfiles,
    refetch,
  };
};
